import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { userFormData } from '../../../constants/user';
import styles from '../styles.module.css';
import {
  handleChangeUserType,
  handleSelectUserType,
  userProfileType
} from './types';
import { statusContainer, UserForm } from './userForm';
import { updateUserSchema } from './utils';
import { LocationsAssignedTable } from '../AssignsTables/locationsTable';
import { LocationData as LocationDataType } from '../LocationsContainer/types';
import { AdminDeleteSection } from '../UtilsContainers/delete';
import { AdminDetailHeader } from '../UtilsContainers/header';
import ConfirmDeleteIcon from '@/assets/icons/confirm-delete.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
import { AdminCardButtons } from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { getZodSchemaErrors } from '@/components/Form/Input/utils';
import { adminToolPaths } from '@/constants/adminTool';
import { PermissionsList } from '@/constants/permissionsList';
import { useModal } from '@/context/ModalContext';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import { useGetLocationsWithAccessLevels } from '@/hooks/services/Locations';
import {
  useDeleteUser,
  useGetUserDetails,
  useUpdateUser
} from '@/hooks/services/Users';
import { RootState } from '@/store';
import { getUsersPermissions } from '@/utils/adminToolPermissions';

function UserDetails() {
  const navigate = useNavigate();
  const { setCustomBreadcrumbArray } = useNavigationBar();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canEdit: canEditUser, canDelete: canDeleteUser } =
    getUsersPermissions(auditableElements, userGroup);

  const { userId: rawUserId } = useParams<{ userId: string }>();
  const userId = rawUserId?.toLowerCase() ?? '';
  const [showEditUser, setShowEditUser] = useState(false);
  const [formData, setFormData] = useState<userProfileType>(userFormData);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [onSubmit, setOnSubmit] = useState(false);

  const {
    data: userProfile,
    isLoading: initialLoading,
    refetch
  } = useGetUserDetails(userId || '');

  const { data: locationsData } = useGetLocationsWithAccessLevels(
    userProfile?.username || ''
  );

  const locationRows: LocationDataType[] =
    locationsData?.data?.map((location: any) => ({
      id: location.id,
      type: location.type || 'power',
      name: location.name,
      category: location.category || 'powersite',
      status: (location.status as 'Active' | 'Inactive') || 'Active',
      serialNumber: location.id,
      geoReference: location.geoReference || {
        latitude: '',
        longitude: ''
      },
      city: location.city,
      state: location.state,
      customer: location.customer || '',
      turbines: location.turbines?.map((t: any) => ({ id: t.id })) || []
    })) || [];

  useEffect(() => {
    if (userProfile) {
      setFormData({
        ...userProfile,
        companyId: userProfile.companyId
          ? Number(userProfile.companyId)
          : undefined
      });
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userProfile?.username) {
      return;
    }
    setCustomBreadcrumbArray({
      label: userProfile.username,
      path: `/admin-tool/users/${userId}`
    });
  }, [userProfile, userId, setCustomBreadcrumbArray]);

  useEffect(() => {
    return () => {
      setCustomBreadcrumbArray({});
    };
  }, [setCustomBreadcrumbArray]);

  const {
    mutate: updateUser,
    isPending,
    error: updateError,
    reset: updateReset
  } = useUpdateUser();

  const {
    mutate: deleteUser,
    isPending: isDeleting,
    error: deleteError,
    reset: deleteReset
  } = useDeleteUser();

  const error = updateError || deleteError;
  const reset = deleteError ? deleteReset : updateReset;

  const { updateModalContent, closeModal, showTypedModal } = useModal();
  const { showToast } = useToast();

  const setParsedZodData = (formData: any) => {
    const parsed = updateUserSchema.safeParse({
      ...formData
    });

    if (!parsed.success) {
      const errs = getZodSchemaErrors(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
    }

    return parsed;
  };

  const onUpdate = () => {
    setOnSubmit(true);
    const parsed = setParsedZodData(formData);

    const updatedData = {
      ...formData,
      phone:
        formData?.phone && formData?.phone?.startsWith('+1')
          ? formData.phone
          : formData.phone
            ? `+1${formData.phone}`
            : ''
    };

    if (parsed.success) {
      setFormErrors({});
      updateUser(updatedData, {
        onSuccess: () => {
          updateModalContent(
            <div className={styles.successModalContainer}>
              <div className={styles.successModalIcon}>
                <SuccessIcon />
              </div>
              <h2 className={styles.successModalTitle}>
                {`User was successfully updated`}
              </h2>
            </div>,
            {
              modalTitle: '',
              modalButtons: [],
              autoClose: 3000
            }
          );
          setTimeout(() => {
            setShowEditUser(false);
            closeModal();
            refetch();
          }, 2000);
        },
        onError: error => {
          console.error('Error saving user:', error);
        }
      });
    }
  };

  const onDelete = () => {
    updateModalContent(null, {
      modalTitle: 'Delete',
      modalIcon: DeleteIcon,
      modalButtons: [
        {
          label: 'CANCEL',
          onClick: () => {
            closeModal();
          },
          variant: 'cancelRed'
        },
        {
          label: 'DELETE PERMANENTLY',
          onClick: () => {
            handleDelete();
          },
          variant: 'okRed',
          isLoader: isDeleting
        }
      ],
      alignButtons: 'center',
      contentTitle: 'You are about to delete',
      contentSubtitle: `${formData.username}`,
      contentDescription: (
        <div className={styles.contentDescription}>
          <span>This action can not be undone.</span>
          <br />
          <span>Are you sure you want to proceed?</span>
        </div>
      )
    });
    return;
  };

  const handleDelete = () => {
    deleteUser(userId || '', {
      onSuccess: () => {
        showTypedModal({
          type: 'info',
          title: 'User was successfully deleted',
          icon: ConfirmDeleteIcon
        });
        setTimeout(() => {
          setShowEditUser(false);
          closeModal();
          navigate(`${adminToolPaths.users.root}`);
        }, 1500);
      }
    });
  };

  const handleChangeFormData: handleChangeUserType = (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  const handleSelectFormData: handleSelectUserType = selectedData => {
    const newFormData = {
      ...formData,
      ...Object.fromEntries(
        selectedData.map(({ field, value }) => [field, value])
      )
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  const onEditUser = () => {
    setShowEditUser(prev => !prev);
  };

  const handleRowClick = (row: LocationDataType) => {
    navigate(
      `${adminToolPaths.locations.detail.replace(':locationId', row.id)}`
    );
  };

  const onCancel = () => {
    setOnSubmit(false);
    setFormErrors({});
    setFormData(userProfile);
    setShowEditUser(false);
  };

  useEffect(() => {
    if (error) {
      showToast(error.message || 'An error occurred', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          reset();
          if (!deleteError) refetch();
        }
      });
    }
  }, [error]);

  if (initialLoading) {
    return <ContainerLoader />;
  }

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminDetailHeader
          title="User Details"
          icon={UserIcon}
          isEditing={showEditUser}
          onEditClick={canEditUser ? onEditUser : undefined}
          editButtonLabel="EDIT"
          right={statusContainer({
            showEditUser,
            formData,
            handleChangeFormData
          })}
        />

        <UserForm
          formData={formData}
          handleChangeFormData={handleChangeFormData}
          handleSelectFormData={handleSelectFormData}
          showEditUser={showEditUser}
          formErrors={formErrors}
          container="edit"
        />
      </div>

      {!showEditUser && (
        <LocationsAssignedTable
          locationsForTable={locationRows}
          onRowClick={handleRowClick}
        />
      )}

      {showEditUser && canDeleteUser && (
        <AdminDeleteSection
          title="Delete User"
          onDeleteClick={onDelete}
          text="This action can not be undone"
          buttonLabel="DELETE USER PERMANENTLY"
          loading={isDeleting}
        />
      )}

      {showEditUser && (
        <AdminCardButtons>
          <Button
            onClick={() => onCancel()}
            color="blue"
            solid={false}
            label="CANCEL"
            size="large"
          />
          <Button
            onClick={() => onUpdate()}
            color="blue"
            label="SAVE"
            size="large"
            loading={isPending}
          />
        </AdminCardButtons>
      )}
    </div>
  );
}

export default UserDetails;
